'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { computeTypingMetrics, TYPING_SAMPLES } from '@/lib/browser-calc-tools-extra';

type Phase = 'idle' | 'running' | 'done';

function pickSampleIndex(previous: number): number {
  if (TYPING_SAMPLES.length <= 1) {
    return 0;
  }
  let next = previous;
  while (next === previous) {
    next = Math.floor(Math.random() * TYPING_SAMPLES.length);
  }
  return next;
}

export default function BrowserTypingSpeedTest() {
  const [sampleIndex, setSampleIndex] = useState(0);
  const [typed, setTyped] = useState('');
  const [phase, setPhase] = useState<Phase>('idle');
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [nowTick, setNowTick] = useState<number>(0);
  const [finalElapsed, setFinalElapsed] = useState<number>(0);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  const target = TYPING_SAMPLES[sampleIndex];

  // Live clock while running. Guarded to the client via useEffect.
  useEffect(() => {
    if (phase !== 'running' || startedAt === null) {
      return;
    }
    const id = window.setInterval(() => {
      setNowTick(Date.now());
    }, 200);
    return () => window.clearInterval(id);
  }, [phase, startedAt]);

  const elapsedMs = useMemo(() => {
    if (phase === 'done') {
      return finalElapsed;
    }
    if (phase === 'running' && startedAt !== null) {
      return Math.max(0, nowTick - startedAt);
    }
    return 0;
  }, [phase, startedAt, nowTick, finalElapsed]);

  const metrics = useMemo(
    () => computeTypingMetrics(target, typed, elapsedMs),
    [target, typed, elapsedMs],
  );

  function handleChange(value: string) {
    if (phase === 'done') {
      return;
    }
    let start = startedAt;
    if (phase === 'idle' && value.length > 0) {
      start = Date.now();
      setStartedAt(start);
      setNowTick(start);
      setPhase('running');
    }
    // Do not allow typing past the end of the sample text.
    const clipped = value.slice(0, target.length);
    setTyped(clipped);
    if (clipped.length >= target.length && start !== null) {
      setFinalElapsed(Math.max(1, Date.now() - start));
      setPhase('done');
    }
  }

  function restart(newSample: boolean) {
    setTyped('');
    setStartedAt(null);
    setNowTick(0);
    setFinalElapsed(0);
    setPhase('idle');
    if (newSample) {
      setSampleIndex((prev) => pickSampleIndex(prev));
    }
    if (typeof window !== 'undefined') {
      window.setTimeout(() => inputRef.current?.focus(), 0);
    }
  }

  const seconds = (elapsedMs / 1000).toFixed(1);

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Typing Speed Test</span>
        <h2 className="tool-live-title">Measure your typing speed and accuracy</h2>
        <p className="tool-live-subtitle">
          Start typing the sample below. Your words-per-minute and accuracy update live, and the
          timer starts on your first keystroke. Everything runs in your browser.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-field tool-live-field-full">
            <span>Sample text</span>
            <div
              aria-label="Sample text to type"
              style={{
                padding: '0.85rem 1rem',
                borderRadius: '0.6rem',
                background: 'rgba(120, 120, 160, 0.1)',
                lineHeight: 1.7,
                fontSize: '1.05rem',
                letterSpacing: '0.01em',
              }}
            >
              {target.split('').map((char, index) => {
                let color: string | undefined;
                let background: string | undefined;
                if (index < typed.length) {
                  const correct = typed[index] === char;
                  color = correct ? '#16a34a' : '#dc2626';
                  if (!correct) {
                    background = 'rgba(220, 38, 38, 0.16)';
                  }
                } else if (index === typed.length && phase !== 'done') {
                  background = 'rgba(96, 120, 240, 0.3)';
                }
                return (
                  <span
                    key={index}
                    style={{
                      color,
                      background,
                      borderRadius: background ? '0.15rem' : undefined,
                    }}
                  >
                    {char}
                  </span>
                );
              })}
            </div>
          </div>

          <div className="tool-live-field tool-live-field-full">
            <span>Type here</span>
            <textarea
              aria-label="Type the sample text here"
              disabled={phase === 'done'}
              onChange={(event) => handleChange(event.target.value)}
              placeholder="Start typing to begin the timer…"
              ref={inputRef}
              rows={4}
              value={typed}
            />
          </div>

          <div className="tool-live-actions">
            <button
              className="button button-secondary"
              onClick={() => restart(false)}
              type="button"
            >
              Restart
            </button>
            <button className="button button-secondary" onClick={() => restart(true)} type="button">
              New text
            </button>
          </div>
        </section>

        <section className="tool-live-preview">
          <div className="tool-html-meta-grid">
            <div className="tool-live-field">
              <span>Net WPM</span>
              <input readOnly type="text" value={String(metrics.netWpm)} />
            </div>
            <div className="tool-live-field">
              <span>Gross WPM</span>
              <input readOnly type="text" value={String(metrics.grossWpm)} />
            </div>
            <div className="tool-live-field">
              <span>Accuracy</span>
              <input readOnly type="text" value={`${metrics.accuracy}%`} />
            </div>
            <div className="tool-live-field">
              <span>Time</span>
              <input readOnly type="text" value={`${seconds}s`} />
            </div>
            <div className="tool-live-field">
              <span>Correct characters</span>
              <input readOnly type="text" value={String(metrics.correctChars)} />
            </div>
            <div className="tool-live-field">
              <span>Mistakes</span>
              <input readOnly type="text" value={String(metrics.incorrectChars)} />
            </div>
          </div>
          {phase === 'done' ? (
            <p className="status-message status-success">
              Finished! Net {metrics.netWpm} WPM at {metrics.accuracy}% accuracy. Press Restart or
              New text to try again.
            </p>
          ) : null}
          <p className="tool-live-panel-note">
            WPM uses the standard of 5 characters per word. Net WPM counts only correct characters.
          </p>
        </section>
      </div>
    </div>
  );
}
